import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../lib/api';
import { playSound } from '../../hooks/useAlertSound';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import {
  Bell, Volume2, VolumeX, Save, Users, Monitor,
  ChevronDown, ChevronUp, Globe, Upload, Trash2, Music, Play, FileAudio, Search
} from 'lucide-react';

import { SOUND_CATALOG, ALL_SOUNDS_FLAT, SOUND_COUNT } from '../../lib/sound-library';

const EVENT_LABELS = {
  new_ticket: { label: 'Tichet Nou Creat', description: 'Alerteaza cand un tichet nou este creat in sistem', category: 'Tichete' },
  ticket_assigned: { label: 'Tichet Asignat', description: 'Alerteaza cand un tichet este asignat sau reasignat', category: 'Tichete' },
  ticket_status_change: { label: 'Schimbare Status Tichet', description: 'Alerteaza la schimbari de status (acceptat, in lucru, rezolvat, etc.)', category: 'Tichete' },
  ticket_comment: { label: 'Comentariu pe Tichet', description: 'Alerteaza cand un comentariu nou este adaugat', category: 'Tichete' },
  stock_receipt: { label: 'Receptie Marfa', description: 'Alerteaza la inregistrarea unei noi receptii de marfa', category: 'WMS - Stocuri' },
  stock_transfer: { label: 'Transfer Stoc', description: 'Alerteaza la miscari si transferuri intre locatii de stoc', category: 'WMS - Stocuri' },
  stock_consumption: { label: 'Consum Stoc', description: 'Alerteaza la consumul de materiale din stoc', category: 'WMS - Stocuri' },
  low_stock_alert: { label: 'Alerta Stoc Minim', description: 'Alerteaza cand un articol scade sub stocul minim', category: 'WMS - Stocuri' },
  pm_created: { label: 'Plan Mentenanta Creat', description: 'Alerteaza la crearea unui nou plan de mentenanta preventiva', category: 'Mentenanta' },
  pm_completed: { label: 'Mentenanta Finalizata', description: 'Alerteaza la finalizarea unei lucrari de mentenanta', category: 'Mentenanta' },
  pm_overdue: { label: 'Mentenanta Intarziata', description: 'Alerteaza cand o lucrare de mentenanta depaseste termenul', category: 'Mentenanta' },
  material_request_created: { label: 'Cerere Material Noua', description: 'Alerteaza la crearea unei noi cereri de materiale', category: 'Cereri Materiale' },
  material_request_approved: { label: 'Cerere Material Aprobata', description: 'Alerteaza cand o cerere de materiale este aprobata', category: 'Cereri Materiale' },
  material_request_rejected: { label: 'Cerere Material Respinsa', description: 'Alerteaza cand o cerere de materiale este respinsa', category: 'Cereri Materiale' },
  revision_overdue: { label: 'Revizie Intarziata', description: 'Alerteaza cand o revizie programata a fost depasita', category: 'Revizii' },
  sla_breach: { label: 'SLA Depasit', description: 'Alerteaza cand un SLA este incalcat sau in risc', category: 'SLA' },
  push_notification: { label: 'Notificare Push (Anunt)', description: 'Alerteaza la primirea unei notificari push de la administrator', category: 'Sistem' },
};

const BUILT_IN_SOUNDS = ALL_SOUNDS_FLAT.map(s => ({ value: s.value, label: s.label, category: s.category }));

const ROLE_LABELS = {
  admin: 'Admin', manager: 'Manager', technician: 'Tehnician',
  section_chief: 'Sef Sectie', operator: 'Operator', procurement: 'Aprovizionare',
  viewer: 'Vizualizator', auditor: 'Auditor',
};
const ROLE_COLORS = {
  admin: 'bg-red-100 text-red-700', manager: 'bg-blue-100 text-blue-700',
  technician: 'bg-green-100 text-green-700', section_chief: 'bg-purple-100 text-purple-700',
  operator: 'bg-amber-100 text-amber-700', procurement: 'bg-cyan-100 text-cyan-700',
  viewer: 'bg-slate-100 text-slate-600', auditor: 'bg-orange-100 text-orange-700',
};

const DEFAULT_EVENTS = {
  new_ticket: { enabled: true, sound_type: 'urgent' },
  ticket_assigned: { enabled: true, sound_type: 'chime' },
  ticket_status_change: { enabled: true, sound_type: 'ping' },
  ticket_comment: { enabled: false, sound_type: 'subtle' },
  stock_receipt: { enabled: true, sound_type: 'rising' },
  stock_transfer: { enabled: false, sound_type: 'subtle' },
  stock_consumption: { enabled: false, sound_type: 'subtle' },
  low_stock_alert: { enabled: true, sound_type: 'siren' },
  pm_created: { enabled: true, sound_type: 'chime' },
  pm_completed: { enabled: true, sound_type: 'rising' },
  pm_overdue: { enabled: true, sound_type: 'emergency' },
  material_request_created: { enabled: true, sound_type: 'normal' },
  material_request_approved: { enabled: true, sound_type: 'chime' },
  material_request_rejected: { enabled: true, sound_type: 'falling' },
  revision_overdue: { enabled: true, sound_type: 'horn' },
  sla_breach: { enabled: true, sound_type: 'emergency' },
  push_notification: { enabled: true, sound_type: 'radar' },
};

// Shared audio context ref for preview
const previewAudioRef = { current: null };

const PreviewButton = ({ soundType, volume, customSounds }) => {
  const handlePlay = () => {
    let customUrl = null;
    if (soundType?.startsWith('custom:')) {
      const id = soundType.replace('custom:', '');
      const snd = customSounds?.find(s => s.id === id);
      if (snd) customUrl = `${process.env.REACT_APP_BACKEND_URL}${snd.url}`;
    }
    playSound(previewAudioRef, soundType, volume, customUrl);
  };
  return (
    <Button variant="outline" size="sm" className="h-8 text-xs gap-1" onClick={handlePlay} data-testid="preview-sound-btn">
      <Play className="w-3 h-3" /> Test
    </Button>
  );
};


const UserSettingsEditor = ({ settings, onChange, customSounds = [] }) => {
  const [soundSearch, setSoundSearch] = useState('');

  const filteredSounds = soundSearch
    ? BUILT_IN_SOUNDS.filter(s => s.label.toLowerCase().includes(soundSearch.toLowerCase()) || s.category.toLowerCase().includes(soundSearch.toLowerCase()))
    : BUILT_IN_SOUNDS;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Volume2 className="w-4 h-4 text-slate-500" />
          <Label className="text-sm font-medium">Sunete Activate</Label>
        </div>
        <Switch
          checked={settings.sound_enabled}
          onCheckedChange={(v) => onChange({ ...settings, sound_enabled: v })}
          data-testid="sound-enabled-switch"
        />
      </div>

      {settings.sound_enabled && (
        <div className="space-y-2">
          <Label className="text-xs text-slate-500">Volum: {settings.volume}%</Label>
          <input
            type="range" min="0" max="100" value={settings.volume}
            onChange={(e) => onChange({ ...settings, volume: parseInt(e.target.value) })}
            className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
            data-testid="volume-slider"
          />
          <div className="flex justify-between text-[10px] text-slate-400">
            <span><VolumeX className="w-3 h-3 inline" /> 0%</span>
            <span><Volume2 className="w-3 h-3 inline" /> 100%</span>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Monitor className="w-4 h-4 text-slate-500" />
          <Label className="text-sm font-medium">Notificari Desktop</Label>
        </div>
        <Switch
          checked={settings.desktop_notifications}
          onCheckedChange={(v) => onChange({ ...settings, desktop_notifications: v })}
          data-testid="desktop-notifications-switch"
        />
      </div>

      <Separator />

      <div className="space-y-3">
        <Label className="text-sm font-semibold text-slate-700">Evenimente</Label>
        {(() => {
          const categories = {};
          Object.entries(EVENT_LABELS).forEach(([key, { label, description, category }]) => {
            if (!categories[category]) categories[category] = [];
            categories[category].push({ key, label, description });
          });
          return Object.entries(categories).map(([catName, events]) => (
            <div key={catName} className="space-y-2">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mt-3 mb-1 border-b pb-1">{catName}</p>
              {events.map(({ key, label, description }) => {
                const eventConfig = settings.events?.[key] || DEFAULT_EVENTS[key] || { enabled: false, sound_type: 'subtle' };
                return (
                  <div key={key} className="p-3 bg-slate-50 rounded-lg space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-800">{label}</p>
                        <p className="text-[11px] text-slate-500">{description}</p>
                      </div>
                      <Switch
                        checked={eventConfig?.enabled ?? false}
                        onCheckedChange={(v) => {
                          const newEvents = { ...settings.events, [key]: { ...eventConfig, enabled: v } };
                          onChange({ ...settings, events: newEvents });
                        }}
                        data-testid={`event-${key}-switch`}
                      />
                    </div>
                    {eventConfig?.enabled && (
                      <div className="flex items-center gap-2 flex-wrap">
                        <Select
                          value={eventConfig.sound_type || 'subtle'}
                          onValueChange={(v) => {
                            const newEvents = { ...settings.events, [key]: { ...eventConfig, sound_type: v } };
                            onChange({ ...settings, events: newEvents });
                          }}
                        >
                          <SelectTrigger className="h-8 text-xs w-64" data-testid={`event-${key}-sound-type`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="max-h-72">
                            {SOUND_CATALOG.map(cat => (
                              <React.Fragment key={cat.category}>
                                <div className="px-2 py-1 text-[10px] font-semibold text-slate-400 uppercase border-t first:border-t-0 sticky top-0 bg-white">{cat.category}</div>
                                {cat.sounds.map(s => (
                                  <SelectItem key={s.value} value={s.value}>
                                    <span className="text-xs">{s.label}</span>
                                  </SelectItem>
                                ))}
                              </React.Fragment>
                            ))}
                            {customSounds.length > 0 && (
                              <>
                                <div className="px-2 py-1 mt-1 text-[10px] font-semibold text-slate-400 uppercase border-t">Sunete personalizate</div>
                                {customSounds.map(s => (
                                  <SelectItem key={`custom:${s.id}`} value={`custom:${s.id}`}>
                                    <span className="flex items-center gap-2 text-xs">
                                      <Music className="w-3 h-3 text-purple-600" />
                                      {s.display_name}
                                    </span>
                                  </SelectItem>
                                ))}
                              </>
                            )}
                          </SelectContent>
                        </Select>
                        {settings.sound_enabled && (
                          <PreviewButton soundType={eventConfig.sound_type || 'subtle'} volume={settings.volume} customSounds={customSounds} />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ));
        })()}
      </div>
    </div>
  );
};


// ============================================================
// Custom Sound Upload Section
// ============================================================
const CustomSoundsSection = ({ customSounds, onSoundsChanged }) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['mp3', 'wav', 'ogg', 'm4a'].includes(ext)) {
      toast.error('Format neacceptat. Folositi: mp3, wav, ogg, m4a');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Fisierul depaseste limita de 2MB');
      return;
    }
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      await api.post('/alert-settings/sounds/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Sunet "${file.name}" incarcat cu succes!`);
      onSoundsChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Eroare la incarcarea fisierului');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (sound) => {
    if (!window.confirm(`Stergeti sunetul "${sound.display_name}"?`)) return;
    try {
      await api.delete(`/alert-settings/sounds/${sound.id}`);
      toast.success('Sunet sters');
      onSoundsChanged();
    } catch {
      toast.error('Eroare la stergere');
    }
  };

  const handlePreviewCustom = (sound) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}${sound.url}`;
    try {
      const audio = new Audio(url);
      audio.volume = 0.5;
      audio.play().catch(() => toast.error('Nu se poate reda sunetul'));
    } catch { /* ignore */ }
  };

  return (
    <Card data-testid="custom-sounds-card">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <FileAudio className="w-5 h-5 text-purple-600" />
          <CardTitle className="text-base">Sunete Personalizate</CardTitle>
        </div>
        <p className="text-xs text-slate-500 mt-1">Incarcati propriile sunete de alerta (mp3, wav, ogg, m4a - max 2MB). Acestea vor fi disponibile in selectorul de sunete.</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".mp3,.wav,.ogg,.m4a"
            className="hidden"
            onChange={handleUpload}
            data-testid="sound-file-input"
          />
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-full border-dashed border-2 h-12 gap-2"
            data-testid="upload-sound-btn"
          >
            {uploading ? (
              <><div className="animate-spin h-4 w-4 border-2 border-purple-500 border-t-transparent rounded-full" /> Se incarca...</>
            ) : (
              <><Upload className="w-4 h-4 text-purple-500" /> Incarca Sunet Personalizat</>
            )}
          </Button>
        </div>

        {customSounds.length === 0 ? (
          <p className="text-xs text-slate-400 text-center py-3">Niciun sunet personalizat incarcat</p>
        ) : (
          <div className="space-y-2">
            {customSounds.map(sound => (
              <div key={sound.id} className="flex items-center gap-3 p-2.5 bg-purple-50 rounded-lg" data-testid={`custom-sound-${sound.id}`}>
                <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <Music className="w-4 h-4 text-purple-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{sound.display_name}</p>
                  <p className="text-[10px] text-slate-400">{sound.original_name} - {(sound.size_bytes / 1024).toFixed(0)} KB - {sound.uploaded_by_name}</p>
                </div>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handlePreviewCustom(sound)} data-testid={`play-custom-${sound.id}`}>
                  <Play className="w-3.5 h-3.5 text-purple-600" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700" onClick={() => handleDelete(sound)} data-testid={`delete-custom-${sound.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};


// ============================================================
// Built-in Sounds Library (preview grid)
// ============================================================
const SoundsLibrary = ({ volume }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCat, setExpandedCat] = useState(null);

  return (
    <Card data-testid="sounds-library-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Volume2 className="w-5 h-5 text-blue-600" />
            <CardTitle className="text-base">Biblioteca Sunete ({SOUND_COUNT} sunete)</CardTitle>
          </div>
        </div>
        <p className="text-xs text-slate-500 mt-1">Click pe "Play" pentru a testa. Sunetele sunt grupate pe {SOUND_CATALOG.length} categorii.</p>
        <div className="relative mt-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Cauta sunet..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200"
            data-testid="sound-search-input"
          />
        </div>
      </CardHeader>
      <CardContent>
        {searchTerm ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {ALL_SOUNDS_FLAT
              .filter(s => s.label.toLowerCase().includes(searchTerm.toLowerCase()) || s.category.toLowerCase().includes(searchTerm.toLowerCase()))
              .map(sound => (
                <button
                  key={sound.value}
                  onClick={() => playSound(previewAudioRef, sound.value, volume)}
                  className="flex items-center gap-2 p-2 bg-slate-50 hover:bg-blue-50 rounded-lg transition-colors text-left group active:scale-95"
                  data-testid={`library-sound-${sound.value}`}
                >
                  <div className="w-7 h-7 rounded-full bg-blue-100 group-hover:bg-blue-200 flex items-center justify-center flex-shrink-0">
                    <Play className="w-3 h-3 text-blue-600" />
                  </div>
                  <div className="min-w-0">
                    <span className="text-[11px] font-medium text-slate-700 leading-tight block truncate">{sound.label}</span>
                    <span className="text-[9px] text-slate-400 block">{sound.category}</span>
                  </div>
                </button>
              ))}
          </div>
        ) : (
          <div className="space-y-2">
            {SOUND_CATALOG.map(cat => {
              const isExpanded = expandedCat === cat.category;
              return (
                <div key={cat.category} className="border rounded-lg overflow-hidden">
                  <button
                    onClick={() => setExpandedCat(isExpanded ? null : cat.category)}
                    className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-slate-50 text-left"
                  >
                    <div className="flex items-center gap-2">
                      <Volume2 className="w-4 h-4 text-blue-500" />
                      <span className="text-sm font-medium text-slate-800">{cat.category}</span>
                      <Badge className="text-[10px] bg-blue-50 text-blue-600">{cat.sounds.length}</Badge>
                    </div>
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                  </button>
                  {isExpanded && (
                    <div className="px-3 pb-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1.5">
                      {cat.sounds.map(sound => (
                        <button
                          key={sound.value}
                          onClick={() => playSound(previewAudioRef, sound.value, volume)}
                          className="flex items-center gap-2 p-2 bg-slate-50 hover:bg-blue-50 rounded-lg transition-colors text-left group active:scale-95"
                          data-testid={`library-sound-${sound.value}`}
                        >
                          <div className="w-7 h-7 rounded-full bg-blue-100 group-hover:bg-blue-200 flex items-center justify-center flex-shrink-0">
                            <Play className="w-3 h-3 text-blue-600" />
                          </div>
                          <span className="text-[11px] font-medium text-slate-700 leading-tight truncate">{sound.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};


// ============================================================
// Main Admin Page
// ============================================================
export default function AlertSettingsAdminPage() {
  const [users, setUsers] = useState([]);
  const [customSounds, setCustomSounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [expandedUser, setExpandedUser] = useState(null);
  const [globalSettings, setGlobalSettings] = useState({
    sound_enabled: true, volume: 80, desktop_notifications: true,
    events: { ...DEFAULT_EVENTS },
  });
  const [applyingGlobal, setApplyingGlobal] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const [usersRes, soundsRes] = await Promise.all([
        api.get('/alert-settings/admin/users'),
        api.get('/alert-settings/sounds'),
      ]);
      setUsers(usersRes.data || []);
      setCustomSounds(soundsRes.data || []);
    } catch {
      toast.error('Eroare la incarcarea setarilor');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSounds = useCallback(async () => {
    try {
      const res = await api.get('/alert-settings/sounds');
      setCustomSounds(res.data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const saveUserSettings = async (userId) => {
    const userObj = users.find(u => u.user_id === userId);
    if (!userObj) return;
    try {
      setSaving(prev => ({ ...prev, [userId]: true }));
      await api.put(`/alert-settings/admin/${userId}`, {
        sound_enabled: userObj.sound_enabled,
        volume: userObj.volume,
        desktop_notifications: userObj.desktop_notifications,
        events: userObj.events,
      });
      toast.success(`Setari salvate pentru ${userObj.full_name}`);
    } catch {
      toast.error('Eroare la salvarea setarilor');
    } finally {
      setSaving(prev => ({ ...prev, [userId]: false }));
    }
  };

  const updateUserLocally = (userId, newSettings) => {
    setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, ...newSettings } : u));
  };

  const applyGlobalSettings = async () => {
    try {
      setApplyingGlobal(true);
      await api.put('/alert-settings/admin/global/apply', globalSettings);
      toast.success('Setari globale aplicate pentru toti utilizatorii!');
      fetchUsers();
    } catch {
      toast.error('Eroare la aplicarea setarilor globale');
    } finally {
      setApplyingGlobal(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6" data-testid="alert-settings-admin-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
          <Bell className="w-5 h-5 text-amber-600" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-slate-900">Configurare Alerte Sonore</h1>
          <p className="text-sm text-slate-500">Gestioneaza sunetele si notificarile desktop pentru toti utilizatorii</p>
        </div>
      </div>

      {/* Sounds Library */}
      <SoundsLibrary volume={globalSettings.volume} />

      {/* Custom sounds upload */}
      <CustomSoundsSection customSounds={customSounds} onSoundsChanged={fetchSounds} />

      {/* Global settings */}
      <Card data-testid="global-settings-card">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-blue-600" />
            <CardTitle className="text-base">Setari Globale (Aplica pentru Toti)</CardTitle>
          </div>
          <p className="text-xs text-slate-500 mt-1">Configurati setarile implicite si aplicati-le tuturor utilizatorilor dintr-un singur click</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <UserSettingsEditor
            settings={globalSettings}
            onChange={setGlobalSettings}
            customSounds={customSounds}
          />
          <Separator />
          <Button
            onClick={applyGlobalSettings}
            disabled={applyingGlobal}
            className="w-full bg-blue-600 hover:bg-blue-700"
            data-testid="apply-global-btn"
          >
            {applyingGlobal ? (
              <><div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" /> Se aplica...</>
            ) : (
              <><Globe className="w-4 h-4 mr-2" /> Aplica Setari pentru Toti Utilizatorii</>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Per-user settings */}
      <Card data-testid="per-user-settings-card">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-green-600" />
            <CardTitle className="text-base">Setari per Utilizator</CardTitle>
          </div>
          <p className="text-xs text-slate-500 mt-1">Personalizati alertele pentru fiecare utilizator in parte</p>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {users.map((u) => (
              <div key={u.user_id} className="border rounded-lg overflow-hidden" data-testid={`user-row-${u.user_id}`}>
                <button
                  className="w-full flex items-center justify-between p-3 hover:bg-slate-50 transition-colors text-left"
                  onClick={() => setExpandedUser(expandedUser === u.user_id ? null : u.user_id)}
                  data-testid={`expand-user-${u.user_id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-sm font-semibold text-slate-600">
                      {(u.full_name || '?')[0]}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">{u.full_name}</p>
                      <p className="text-[11px] text-slate-400">{u.email}</p>
                    </div>
                    <Badge className={`text-[10px] ${ROLE_COLORS[u.role] || 'bg-slate-100 text-slate-600'}`}>
                      {ROLE_LABELS[u.role] || u.role}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    {u.sound_enabled ? <Volume2 className="w-4 h-4 text-green-500" /> : <VolumeX className="w-4 h-4 text-slate-300" />}
                    {expandedUser === u.user_id ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                  </div>
                </button>

                {expandedUser === u.user_id && (
                  <div className="px-4 pb-4 border-t bg-white pt-4">
                    <UserSettingsEditor
                      settings={{
                        sound_enabled: u.sound_enabled,
                        volume: u.volume,
                        desktop_notifications: u.desktop_notifications,
                        events: u.events || { ...DEFAULT_EVENTS },
                      }}
                      onChange={(newSettings) => updateUserLocally(u.user_id, newSettings)}
                      customSounds={customSounds}
                    />
                    <div className="mt-4">
                      <Button
                        onClick={() => saveUserSettings(u.user_id)}
                        disabled={saving[u.user_id]}
                        className="w-full"
                        data-testid={`save-user-${u.user_id}`}
                      >
                        {saving[u.user_id] ? (
                          <><div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" /> Se salveaza...</>
                        ) : (
                          <><Save className="w-4 h-4 mr-2" /> Salveaza Setari pentru {u.full_name}</>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
